import React, { useEffect, useRef, useState, useCallback } from "react";
import { Eraser, PenTool } from "lucide-react";

/**
 * Sprint 7 — Signature pad. HTML5 canvas, no extra deps.
 * Calls `onChange(dataUrl, hasInk)` whenever the user finishes a stroke.
 * `dataUrl` is a `data:image/png;base64,...` string (or "" when cleared).
 */
export const SignaturePad = ({ value, onChange, height = 180, accent = "#f59e0b" }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  const getCtx = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return null;
    const ctx = c.getContext("2d");
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = "#fafafa";
    return ctx;
  }, []);

  // Reset / load a value when the parent clears the pad
  useEffect(() => {
    if (!value) {
      const c = canvasRef.current;
      if (c) {
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, c.width, c.height);
        setHasInk(false);
      }
    }
  }, [value]);

  // Size canvas to its container with HiDPI support. Also observe the
  // container so the canvas re-sizes when the parent section becomes
  // visible (e.g. an accordion expands) — otherwise strokes drawn on a
  // 0-width canvas produce a black/blank pad on mobile Android.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = c.getBoundingClientRect();
      if (rect.width <= 0) return; // wait until visible
      c.width = Math.max(1, Math.floor(rect.width * dpr));
      c.height = Math.max(1, Math.floor(height * dpr));
      c.style.height = height + "px";
      const ctx = c.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0); // reset previous scale
      ctx.scale(dpr, dpr);
    };
    resize();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(resize);
      ro.observe(c);
      window.addEventListener("orientationchange", resize);
      return () => { ro.disconnect(); window.removeEventListener("orientationchange", resize); };
    }
    window.addEventListener("resize", resize);
    window.addEventListener("orientationchange", resize);
    return () => { window.removeEventListener("resize", resize); window.removeEventListener("orientationchange", resize); };
  }, [height]);

  const pointFrom = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const isTouch = e.touches && e.touches[0];
    const src = isTouch ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = pointFrom(e);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = getCtx();
    const p = pointFrom(e);
    if (!ctx || !lastPointRef.current) return;
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
    if (!hasInk) setHasInk(true);
  };

  const endDraw = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    const c = canvasRef.current;
    if (!c) return;
    // Encode the signature as a small JPEG on a downscaled off-screen canvas.
    // The full-resolution canvas (with HiDPI ~3x) can produce a multi-MB PNG
    // that crashes the WebView on Android — turning the page black. A
    // downscaled JPEG stays under ~50 kB for a typical signature and remains
    // perfectly legible.
    let dataUrl = "";
    try {
      const MAX_W = 600;
      const scale = Math.min(1, MAX_W / c.width);
      const off = document.createElement("canvas");
      off.width = Math.max(1, Math.round(c.width * scale));
      off.height = Math.max(1, Math.round(c.height * scale));
      const octx = off.getContext("2d");
      // Fill white so JPEG (no alpha) still shows a clean signature
      octx.fillStyle = "#ffffff";
      octx.fillRect(0, 0, off.width, off.height);
      octx.drawImage(c, 0, 0, off.width, off.height);
      dataUrl = off.toDataURL("image/jpeg", 0.85);
    } catch (e) {
      // Fallback to PNG if the browser refuses JPEG for some reason
      try { dataUrl = c.toDataURL("image/png"); } catch (_) { dataUrl = ""; }
    }
    onChange && onChange(dataUrl, true);
  };

  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange && onChange("", false);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-zinc-400 text-xs">
          <PenTool className="w-3.5 h-3.5" />
          <span>Assine no quadro abaixo</span>
        </div>
        <button
          type="button"
          onClick={clear}
          className="text-xs text-zinc-500 hover:text-zinc-200 flex items-center gap-1"
          data-testid="signature-clear"
        >
          <Eraser className="w-3.5 h-3.5" /> Limpar
        </button>
      </div>
      <div
        className="bg-zinc-900 border-2 border-dashed rounded-xl overflow-hidden relative"
        style={{ borderColor: hasInk ? accent : "rgba(255,255,255,0.08)" }}
      >
        <canvas
          ref={canvasRef}
          className="w-full block touch-none cursor-crosshair"
          style={{ height }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
          data-testid="signature-canvas"
        />
        {!hasInk && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-zinc-700 text-sm italic">desenhe a sua assinatura aqui</span>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-[11px]">
        Esta assinatura é uma assinatura electrónica simples (Reg. UE 910/2014). Será incluída no PDF do Termo de Responsabilidade.
      </p>
    </div>
  );
};

export default SignaturePad;
